import { ChangeDetectorRef, Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import { ControlValueAccessorDirective } from '@shared/base-classes/control-value-accessor.directive';
import { TlkSelectComponent, ConnectedPosition } from '@itero/ui-toolkit-angular/select';
import { DropDownItem } from '@shared/models/drop-down-item';
import { CancelEvent, getOptionClassName, getOptionId, ICancelableControl, idNameCompareWith } from '@shared/utils/selectors-utils';
import { TranslateModule } from '@ngx-translate/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';

@Component({
	selector: 'rx-select',
	template: `
		<tlk-select
			[id]="'rx-auto-select-' + id"
			[label]="composedLabel"
			[placeholder]="placeholder ?? ''"
			[formControl]="control"
			[options]="options ?? []"
			[required]="isRequired"
			[isDisabled]="isDisabled || isReadOnly"
			(disableChangeEvent)="handleDisableChange($event)"
			[cancelSelectionText]="isCancellable ? ('Selector.ClearSelection' | translate) : ''"
			[hintText]="hintText ?? ''"
			[requiredText]="'Validation.RequiredField' | translate"
			[autoClose]="autoClose"
			[dropdownPositions]="dropdownPositions ?? 'left'"
			[panelWidth]="panelWidth"
			[panelClass]="panelClass ?? ''"
			[panelStyle]="panelStyle ?? {}"
			[tabIndex]="tabIndex"
			(openEvent)="openEvent.emit($event)"
			(closeEvent)="closeEvent.emit($event)"
			(cancelSelectionEvent)="handleCancelSelection($event)"
			[compareWith]="compareWith"
			[getOptionId]="getOptionId"
			[getOptionName]="getOptionName"
			[getOptionClassName]="getOptionClassName"
			[getOptionStyle]="getOptionStyle"
			[flexibleDimensions]="flexibleDimensions"
		></tlk-select>
	`,
	standalone: true,
	imports: [TlkSelectComponent, TranslateModule, ReactiveFormsModule],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: SelectComponent,
			multi: true
		}
	]
})
export class SelectComponent<T = DropDownItem> extends ControlValueAccessorDirective<T> implements ICancelableControl<T> {
	constructor(
		injector: Injector,
		public cdr: ChangeDetectorRef
	) {
		super(injector);
	}

	/**
	 * Clears the current selection. Part of {@link ICancelableControl}. Note the underlying `tlk-select`
	 * already clears its own value on cancel; this makes the clear explicit for parents that hold the value
	 * elsewhere or invoke it programmatically.
	 */
	handleCancelDefault(event: Event): void {
		event.stopPropagation();
		this.control.patchValue(null as unknown as T);
	}

	/**
	 * Re-emits the raw cancel event for simple listeners, and additionally emits {@link cancelSelection}
	 * (with a reference to this control) so parents can run extra cleanup. The value itself is cleared by
	 * the underlying `tlk-select`.
	 */
	handleCancelSelection(event: MouseEvent): void {
		this.cancelSelectionEvent.emit(event);
		this.cancelSelection.emit({ event, selector: this });
	}

	handleDisableChange(isDisabled: boolean): void {
		if (isDisabled && this.isDisabled && this.cleanOnDisable) {
			this.control.patchValue(null as unknown as T);
		}

		this.disableChangeEvent.emit(isDisabled);
	}

	@Input()
	id = '';

	@Input()
	label?: string;

	/** Optional secondary label appended as "label - secondaryLabel". */
	@Input()
	secondaryLabel?: string;

	/** Optional separator appended to the end of the label (e.g. ":"). */
	@Input()
	labelSeparator?: string;

	/** Read-only is presented as a disabled select (matches the legacy object-selector behavior). */
	@Input()
	isReadOnly = false;

	@Input()
	placeholder?: string;

	/** Composes the displayed label from label + optional secondaryLabel + optional separator. */
	get composedLabel(): string {
		const base = this.label ?? '';
		const secondary = this.secondaryLabel ? ` - ${this.secondaryLabel}` : '';
		const separator = this.labelSeparator ?? '';

		return base + secondary + separator;
	}

	@Input()
	options?: T[];

	@Input()
	isRequired = false;

	@Input()
	isDisabled = false;

	@Input()
	isCancellable = false;

	@Input()
	hintText?: string;

	@Input()
	autoClose = true;

	@Input()
	flexibleDimensions = true;

	@Input()
	dropdownPositions?: ConnectedPosition[] | 'left' | 'right';

	@Input()
	panelWidth: string | number | null = 'auto';

	@Input()
	panelClass?: string | string[] | Set<string> | { [key: string]: any };

	@Input()
	panelStyle?: { [p: string]: any };

	@Input()
	tabIndex?: number | string;

	@Output()
	openEvent = new EventEmitter<void>();

	@Output()
	closeEvent = new EventEmitter<void>();

	@Output()
	cancelSelectionEvent = new EventEmitter<MouseEvent>();

	/**
	 * Emitted when the clear-selection action fires and a parent wants to handle it (e.g. also clear
	 * dependent controls). When no listener is attached, the selection is cleared by default.
	 */
	@Output()
	cancelSelection = new EventEmitter<CancelEvent<T>>();

	@Output()
	disableChangeEvent = new EventEmitter<boolean>();

	/** When set, clears the control's value whenever the select becomes disabled. */
	@Input()
	cleanOnDisable = false;

	@Input()
	compareWith: (o1: T, o2: T) => boolean = idNameCompareWith as unknown as (o1: T, o2: T) => boolean;

	@Input()
	getOptionId = (index: number, option: T) => {
		const item = option as DropDownItem;

		return getOptionId(item?.Name, item?.Id, this.label ?? '');
	};

	@Input()
	getOptionName: (currentOption: T) => string = (currentOption: T) => (currentOption as DropDownItem)?.Name ?? '';

	@Input()
	getOptionClassName: (index: number, option: T) => string | string[] | Set<string> | { [key: string]: any } = (
		index: number,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		option: T
	) => {
		return getOptionClassName(index);
	};

	@Input()
	getOptionStyle: (index: number, option: T) => { [p: string]: any } | null | undefined = (i: number, option: T) => {
		return (option as DropDownItem)?.Hidden ? { display: 'none' } : null;
	};
}
